import React, { useState } from 'react';
import { CheckCircle2, HelpCircle, Loader2, Send } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { describeChange } from '@/lib/contractAmendments';

// ── An updated agreement, one change at a time ──────────────────────────────
// A driver who signed the previous version reads only what changed: each
// amendment's summary and exact wording, and agrees it or asks a question.
// Agreeing goes through contract_amendment_agree(), which records the time;
// signing stays locked (server-side) until every amendment is agreed.

function Wording({ label, text, tone }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${tone}`}>
      <p className="mb-1 font-sans text-[10px] font-black uppercase tracking-widest opacity-70">{label}</p>
      <p className="whitespace-pre-wrap font-serif text-[15px] leading-relaxed">{text}</p>
    </div>
  );
}

function AmendmentCard({ amendment, agreed, questions, contractId, onChange }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [question, setQuestion] = useState('');

  const agree = async () => {
    setBusy(true);
    const { error } = await supabase.rpc('contract_amendment_agree', {
      p_contract_id: contractId, p_amendment_id: amendment.id, p_user_agent: navigator.userAgent,
    });
    setBusy(false);
    if (error) { toast({ title: 'Not recorded', description: error.message, variant: 'destructive' }); return; }
    onChange();
  };

  const ask = async () => {
    setBusy(true);
    const { error } = await supabase.rpc('contract_amendment_ask', {
      p_contract_id: contractId, p_amendment_id: amendment.id, p_question: question,
    });
    setBusy(false);
    if (error) { toast({ title: 'Question not sent', description: error.message, variant: 'destructive' }); return; }
    toast({ title: 'Question sent', description: 'The office will reply here. You can agree once you are happy.' });
    setQuestion('');
    setAsking(false);
    onChange();
  };

  return (
    <div className={`rounded-3xl border p-6 md:p-8 ${agreed ? 'border-green-200 bg-green-50/40' : 'border-slate-200 bg-white'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-primary">Change {amendment.position} · clause {amendment.clause_ref}</p>
          <h3 className="mt-1 text-xl font-black tracking-tight text-slate-900">{amendment.title}</h3>
        </div>
        {agreed && <span className="flex items-center gap-1.5 rounded-full bg-green-600 px-3 py-1 text-xs font-bold text-white"><CheckCircle2 size={14} />Agreed</span>}
      </div>
      <p className="mt-3 font-medium leading-relaxed text-slate-600">{amendment.summary}</p>

      <div className="mt-5 space-y-3">
        {amendment.changes.map((c, i) => {
          const d = describeChange(c);
          if (d.kind === 'removed') return <Wording key={i} label="Removed" text={d.before} tone="border-slate-200 bg-slate-50 text-slate-500 line-through decoration-slate-300" />;
          return (
            <div key={i} className="grid gap-3 md:grid-cols-2">
              {d.before
                ? <Wording label={d.kind === 'removed' ? 'Removed' : 'Was'} text={d.before} tone="border-slate-200 bg-slate-50 text-slate-500" />
                : <Wording label="Was" text="(nothing: this is new)" tone="border-slate-200 bg-slate-50 text-slate-400" />}
              {d.after
                ? <Wording label={d.kind === 'added' ? 'Added' : 'Now'} text={d.after} tone="border-primary/20 bg-primary/5 text-slate-800" />
                : <Wording label="Now" text="(removed)" tone="border-slate-200 bg-white text-slate-400" />}
            </div>
          );
        })}
      </div>

      {questions.length > 0 && (
        <div className="mt-5 space-y-2">
          {questions.map((q) => (
            <div key={q.id} className="rounded-xl bg-amber-50 px-4 py-3 text-sm">
              <p className="font-semibold text-amber-900">You asked: {q.question}</p>
              <p className="mt-1 text-amber-800">{q.answer ? `Parcelink replied: ${q.answer}` : 'Waiting for a reply from the office.'}</p>
            </div>
          ))}
        </div>
      )}

      {!agreed && (
        <div className="mt-6 space-y-3">
          {asking && (
            <div className="space-y-2">
              <textarea
                rows={3}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                maxLength={2000}
                placeholder="What would you like to ask about this change?"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/20"
              />
              <Button onClick={ask} disabled={busy || !question.trim()} className="h-11 rounded-xl">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <><Send size={16} className="mr-2" />Send question</>}
              </Button>
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            <Button onClick={agree} disabled={busy} className="h-12 rounded-xl px-6 font-bold">
              {busy && !asking ? <Loader2 className="animate-spin" size={16} /> : <><CheckCircle2 size={16} className="mr-2" />I agree to this change</>}
            </Button>
            {!asking && (
              <Button variant="outline" onClick={() => setAsking(true)} disabled={busy} className="h-12 rounded-xl px-6 font-bold">
                <HelpCircle size={16} className="mr-2" />I have a question
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Every amendment of the version, in order, with what the driver has agreed and asked. */
export default function AmendmentReview({ contractId, amendments, acceptances, questions, onChange }) {
  const agreedIds = new Set((acceptances || []).map((a) => a.amendment_id));
  const left = amendments.filter((a) => !agreedIds.has(a.id)).length;
  return (
    <div className="space-y-6">
      <div className={`rounded-2xl px-5 py-4 text-sm font-semibold ${left ? 'bg-slate-100 text-slate-700' : 'bg-green-50 text-green-800'}`}>
        {left
          ? `${amendments.length - left} of ${amendments.length} changes agreed. Agree each one to continue.`
          : `All ${amendments.length} changes agreed. Confirm your details and sign below.`}
      </div>
      {amendments.map((a) => (
        <AmendmentCard
          key={a.id}
          amendment={a}
          agreed={agreedIds.has(a.id)}
          questions={(questions || []).filter((q) => q.amendment_id === a.id)}
          contractId={contractId}
          onChange={onChange}
        />
      ))}
    </div>
  );
}
